// skills-frontend-development/src/Pages/Testimonials/index.tsx
import React, { useEffect, useState, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import ApiClient from '../../methods/api/apiClient';
import { useSelector } from 'react-redux';
import { FaTrophy, FaHandshake, FaUsers, FaProjectDiagram, FaList, FaInbox } from 'react-icons/fa';
import loader from '../../methods/loader';
import { toast } from 'react-toastify';
import Layout from '../../components/global/layout';
import environment from '../../environment';
import * as d3 from 'd3';

const Testimonials = () => {
    const user = useSelector((state: any) => state.user);
    const history = useNavigate();
    const [searchParams] = useSearchParams();

    // cw: only GL/AGL/TY Tracker can view this page
    useEffect(() => {
        const roleId = user?.customerRole?._id || user?.customerRole;
        if (roleId && !environment.thankYouRoleIds?.includes(roleId)) {
            history('/testimonials-individual');
        }
    }, [user]);

    const [stats, setStats] = useState<any>(null);
    const [members, setMembers] = useState<any[]>([]);
    const [leaderboard, setLeaderboard] = useState<any[]>([]);
    const [selectedMember, setSelectedMember] = useState('');
    const [label, setLabel] = useState<string | null>(null);
    const [hubThreshold, setHubThreshold] = useState<number | null>(null);
    const [superhubThreshold, setSuperhubThreshold] = useState<number | null>(null);
    const [loading, setLoading] = useState(false);
    const [isLoading, setIsLoading] = useState(true);
    const [viewMode, setViewMode] = useState<'leaderboard' | 'graph'>(searchParams.get('view') === 'graph' ? 'graph' : 'leaderboard');
    const [pendingCount, setPendingCount] = useState(0);
    const [graphData, setGraphData] = useState<any>(null);
    const [graphMonths, setGraphMonths] = useState(6);
    const svgRef = useRef<SVGSVGElement>(null);
    
    useEffect(() => {
        getData();
    }, []);
    useEffect(() => {
        const fetchGraph = async () => {
            const graphRes = await ApiClient.get('api/testimonial/network-graph', { months: graphMonths });
            if (graphRes.success && graphRes.data) {
                setGraphData(graphRes.data);
            }
        };
        if (viewMode === 'graph') {
            fetchGraph();
        }
    }, [graphMonths]);
    // cw: also depend on isLoading/label — on a direct graph load the page first shows a loading
    // screen (svg not mounted), so graphData can arrive before the svg exists. When the loading gate
    // opens and the svg finally mounts, this re-runs and draws it. Without this the graph stays blank
    // until you leave and re-enter the view.
    useEffect(() => {
        if (viewMode === 'graph' && graphData && svgRef.current) {
            try {
                renderGraph();
            } catch (err) {
                console.error('Graph render error:', err);
            }
        }
    }, [viewMode, graphData, isLoading, label]);

    const getData = async () => {
        setLoading(true);
        loader(true);
        
        try {
            // Get members
            const membersRes = await ApiClient.get('api/testimonial/members');
            if (membersRes.success) {
                setMembers(membersRes.data || []);
            }

            // Get testimonial stats
            const statsRes = await ApiClient.get('api/testimonial/stats');
            if (statsRes.success && statsRes.data) {
                setStats(statsRes.data);
                setLabel(statsRes.data.label || user?.testimonialLabel || 'Thank Yous');
                setHubThreshold(statsRes.data.hubThreshold || user?.hubThreshold || 5);
                setSuperhubThreshold(statsRes.data.superhubThreshold || user?.superhubThreshold || 10);
            }

            // Get leaderboard
            const leaderboardRes = await ApiClient.get('api/testimonial/leaderboard');
            if (leaderboardRes.success && leaderboardRes.data) {
                setLeaderboard(leaderboardRes.data.members || []);
                if (leaderboardRes.data.label) setLabel(leaderboardRes.data.label);
                if (leaderboardRes.data.hubThreshold) setHubThreshold(leaderboardRes.data.hubThreshold);
                if (leaderboardRes.data.superhubThreshold) setSuperhubThreshold(leaderboardRes.data.superhubThreshold);
            }

            // Get network graph data
            const graphRes = await ApiClient.get('api/testimonial/network-graph', { months: graphMonths });
            if (graphRes.success && graphRes.data) {
                setGraphData(graphRes.data);
            }

            // Get pending (queued) TYs for the group
            const groupId = user?.groupId?._id || user?.groupId;
            if (groupId) {
                const queueRes = await ApiClient.get('api/testimonial/queue', { groupId });
                if (queueRes.success) {
                    setPendingCount((queueRes.data || []).length);
                }
            }
        } catch (error: any) {
            console.error('Error loading data:', error);
        } finally {
            loader(false);
            setLoading(false);
            setIsLoading(false);
        }
    };

    const giveTestimonial = async () => {
        if (!selectedMember) {
            toast.error('Please select a member');
            return;
        }
        
        loader(true);
        try {
            const res = await ApiClient.post('api/testimonial/give', {
                receivedById: selectedMember
            });
            
            if (res.success) {
                toast.success(`${displayLabel.replace(/s$/, '')} given successfully!`);
                setSelectedMember('');
                getData();
            } else {
                toast.error(res.message || `Failed to give ${displayLabel.toLowerCase()}`);
            }
        } catch (error: any) {
            toast.error(error.message || `Failed to give ${displayLabel.toLowerCase()}`);
        } finally {
            loader(false);
        }
    };

    const getDisplayName = (member: any) => {
        return member.fullName || 
               `${member.firstName || ''} ${member.lastName || ''}`.trim() || 
               member.email;
    };

    const getMemberTypeColor = (memberType: string) => {
        switch (memberType) {
            case 'superhub': return '#FFD700'; // Gold/Yellow
            case 'hub': return '#22C55E'; // Green
            case 'guest': return '#F97316'; // Orange
            default: return '#3B82F6'; // Blue for regular members
        }
    };

    const getMemberTypeBadge = (memberType: string) => {
        switch (memberType) {
            case 'superhub': return '⭐ Superhub';
            case 'hub': return '🟢 Hub';
            case 'guest': return '🟠 Guest';
            default: return '';
        }
    };

    const renderGraph = () => {
        if (!svgRef.current || !graphData) return;
        if (!graphData.nodes?.length || !graphData.edges?.length) return;

        try {
        const svg = d3.select(svgRef.current);
        svg.selectAll('*').remove();

        const width = svgRef.current.clientWidth || 800;
        const height = 600;

        svg.attr('width', width).attr('height', height);

        const nodes = graphData.nodes.map((n: any) => ({ ...n }));
        const links = graphData.edges.map((e: any) => ({ 
            source: e.from, 
            target: e.to,
            id: e.id 
        }));

        // Size each node just big enough to contain its two-line name label
        const LABEL_FONT_SIZE = 10;
        const LABEL_LINE_HEIGHT = LABEL_FONT_SIZE * 1.15;
        const APPROX_CHAR_WIDTH = LABEL_FONT_SIZE * 0.58;
        const splitName = (label: string) => {
            const parts = String(label || '').trim().split(/\s+/);
            const first = parts[0] || '';
            const last = parts.slice(1).join(' ');
            return { first, last };
        };
        const computeRadius = (d: any) => {
            const { first, last } = splitName(d.label);
            const widest = Math.max(first.length, last.length) * APPROX_CHAR_WIDTH;
            const h = (last ? 2 : 1) * LABEL_LINE_HEIGHT;
            const r = Math.sqrt((widest / 2) ** 2 + (h / 2) ** 2) + 4;
            return Math.max(r, 12);
        };
        // Uniform size: use the max radius needed across all nodes, but cap
        // it so the circle never grows large enough to crowd the canvas.
        const MAX_UNIFORM_RADIUS = 28;
        let uniformRadius = 14;
        nodes.forEach((d: any) => {
            uniformRadius = Math.max(uniformRadius, computeRadius(d));
        });
        uniformRadius = Math.min(uniformRadius, MAX_UNIFORM_RADIUS);
        nodes.forEach((d: any) => { d._radius = uniformRadius; });

        const nodeRadius = (_d: any) => uniformRadius;
        const isHub = (d: any) => d.memberType === 'hub' || d.memberType === 'superhub';

        // Create arrow marker (points to the thanked person, i.e. target)
        svg.append('defs').append('marker')
            .attr('id', 'arrowhead')
            .attr('viewBox', '0 -5 10 10')
            .attr('refX', 10)
            .attr('refY', 0)
            .attr('orient', 'auto')
            .attr('markerWidth', 8)
            .attr('markerHeight', 8)
            .attr('xoverflow', 'visible')
            .append('path')
            .attr('d', 'M 0,-5 L 10,0 L 0,5')
            .attr('fill', '#7c3aed')
            .style('stroke', 'none');

            const simulation = d3.forceSimulation(nodes)
            .force('link', d3.forceLink(links).id((d: any) => d.id).distance(130).strength(0.9))
            .force('charge', d3.forceManyBody().strength((d: any) => isHub(d) ? -220 : -120))
            .force('center', d3.forceCenter(width / 2, height / 2))
            .force('collision', d3.forceCollide().radius((d: any) => nodeRadius(d) + 6))
            // Pull hubs/superhubs toward center; push others outward in a ring
            .force('radial', d3.forceRadial(
                (d: any) => {
                    if (d.memberType === 'superhub') return 0;
                    if (d.memberType === 'hub') return 80;
                    if (d.memberType === 'guest') return Math.min(width, height) * 0.42;
                    return Math.min(width, height) * 0.3;
                },
                width / 2,
                height / 2
            ).strength((d: any) => isHub(d) ? 0.35 : 0.15));

        simulation.stop();
        for (let i = 0; i < 300; i++) simulation.tick();

        let wasDragged = false;

        const dragstarted = (event: any) => {
            wasDragged = false;
            if (!event.active) simulation.alphaTarget(0.3).restart();
            event.subject.fx = event.subject.x;
            event.subject.fy = event.subject.y;
        };

        const dragged = (event: any) => {
            wasDragged = true;
            event.subject.fx = event.x;
            event.subject.fy = event.y;
        };

        const dragended = (event: any) => {
            if (!event.active) simulation.alphaTarget(0);
        };

        const link = svg.append('g')
            .selectAll('line')
            .data(links)
            .join('line')
            .attr('stroke', '#a78bfa')
            .attr('stroke-opacity', 0.7)
            .attr('stroke-width', 1.5)
            .attr('marker-end', 'url(#arrowhead)');

        const node = svg.append('g')
            .selectAll('g')
            .data(nodes)
            .join('g')
            .call(d3.drag<any, any>()
                .on('start', dragstarted)
                .on('drag', dragged)
                .on('end', dragended) as any);

        node.style('cursor', (d: any) => d.memberType === 'guest' ? 'default' : 'pointer')
            .on('click', (event: any, d: any) => {
                if (wasDragged) return;
                if (d.memberType === 'guest') return;
                history(`/user-profile/${d.id}?from=graph`);
            });

        node.append('circle')
            .attr('r', (d: any) => nodeRadius(d))
            .attr('fill', (d: any) => getMemberTypeColor(d.memberType))
            .attr('stroke', '#fff')
            .attr('stroke-width', 2);

        // Shrink font per-node so the longest name fits inside the uniform circle.
        const fontSizeFor = (d: any) => {
            const { first, last } = splitName(d.label);
            const longest = Math.max(first.length, last.length, 1);
            // Usable width inside the circle (leave a small margin)
            const usable = (uniformRadius - 2) * 2;
            const fit = usable / (longest * 0.58);
            return Math.max(6, Math.min(LABEL_FONT_SIZE, fit));
        };

        const label = node.append('text')
            .attr('font-size', (d: any) => `${fontSizeFor(d)}px`)
            .attr('font-weight', '600')
            .attr('text-anchor', 'middle')
            .attr('fill', '#fff')
            .attr('pointer-events', 'none')
            .attr('style', 'user-select: none;');

        label.each(function (d: any) {
            const { first, last } = splitName(d.label);
            const el = d3.select(this);
            if (last) {
                el.append('tspan').attr('x', 0).attr('dy', '-0.1em').text(first);
                el.append('tspan').attr('x', 0).attr('dy', '1.1em').text(last);
            } else {
                el.append('tspan').attr('x', 0).attr('dy', '0.35em').text(first);
            }
        });

        node.append('title')
            .text((d: any) => `${d.label}\nReceived: ${d.receivedCount}\nGiven: ${d.givenCount}`);

        const updatePositions = () => {
            link
                .attr('x1', (d: any) => d.source.x)
                .attr('y1', (d: any) => d.source.y)
                .attr('x2', (d: any) => {
                    const dx = d.target.x - d.source.x;
                    const dy = d.target.y - d.source.y;
                    const dist = Math.sqrt(dx * dx + dy * dy) || 1;
                    const r = nodeRadius(d.target) + 4;
                    return d.target.x - (dx * r) / dist;
                })
                .attr('y2', (d: any) => {
                    const dx = d.target.x - d.source.x;
                    const dy = d.target.y - d.source.y;
                    const dist = Math.sqrt(dx * dx + dy * dy) || 1;
                    const r = nodeRadius(d.target) + 4;
                    return d.target.y - (dy * r) / dist;
                });

            node.attr('transform', (d: any) => `translate(${d.x},${d.y})`);
        };

        updatePositions();
        simulation.on('tick', updatePositions);

    } catch (err) {
            console.error('Graph render error:', err);
        }
    };

    // Filter out members who already received testimonials from current user
    const availableMembers = members.filter(m => {
        const memberId = m._id;
        return !stats?.givenTo?.some((given: any) => given._id === memberId);
    });

    // Derive display values: use API data if loaded, else Redux, else defaults
    const displayLabel = label ?? user?.testimonialLabel ?? 'Testimonials';
    const displayHubThreshold = hubThreshold ?? user?.hubThreshold ?? 5;
    const displaySuperhubThreshold = superhubThreshold ?? user?.superhubThreshold ?? 10;

    if (isLoading || label === null) {
        return (
            <Layout>
                <div className='mb-6'>
                    <h3 className="text-2xl font-semibold text-[#111827]">
                        {user?.testimonialLabel || 'Thank Yous'} Group
                    </h3>
                    <p className="text-sm font-normal text-[#75757A]">Loading...</p>
                </div>
                <div className="text-center py-8">
                    <img src="/assets/img/loader.gif" className="pageLoader" alt="Loading..." />
                </div>
            </Layout>
        );
    }

    return (
        <Layout>
            <div className="pprofile1">
                <div className="flex items-center justify-between mb-8">
                    <div>
                        <h3 className="text-2xl font-semibold text-[#111827]">
                            {displayLabel} Group
                        </h3>
                        <p className="text-sm font-normal text-[#75757A]">
                            Group leaderboard, network graph, and {displayLabel.toLowerCase()} management
                        </p>
                    </div>
                    {leaderboard.length > 0 && (
                        <div className="flex items-center gap-2 text-sm text-gray-600">
                            <FaUsers className="text-lg" />
                            <span>{leaderboard.length} members in your group</span>
                        </div>
                    )}
                </div>

                {/* Stats Cards — group totals (sum across all members) */}
                {(() => {
                    const groupGiven = leaderboard.reduce((s: number, m: any) => s + (m.givenCount || 0), 0);
                    const groupReceived = leaderboard.reduce((s: number, m: any) => s + (m.receivedCount || 0), 0);
                    return (
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
                    <div className="bg-white rounded-lg p-6 shadow-sm border">
                        <div className="flex items-center justify-between">
                            <div>
                                <p className="text-sm text-gray-600 mb-1">Given</p>
                                <h3 className="text-3xl font-bold text-[#111827]">
                                    {groupGiven}
                                </h3>
                            </div>
                            <FaHandshake className="text-4xl text-orange-500 opacity-50" />
                        </div>
                    </div>

                    <div className="bg-white rounded-lg p-6 shadow-sm border">
                        <div className="flex items-center justify-between">
                            <div>
                                <p className="text-sm text-gray-600 mb-1">Received</p>
                                <h3 className="text-3xl font-bold text-[#111827]">
                                    {groupReceived}
                                </h3>
                            </div>
                            <FaTrophy className="text-4xl text-green-500 opacity-50" />
                        </div>
                    </div>

                    <div className="bg-white rounded-lg p-6 shadow-sm border">
                        <div className="flex items-center justify-between">
                            <div>
                                <p className="text-sm text-gray-600 mb-1">Pending</p>
                                <h3 className="text-3xl font-bold text-[#111827]">
                                    {pendingCount}
                                </h3>
                            </div>
                            <FaInbox className="text-4xl text-blue-500 opacity-50" />
                        </div>
                    </div>
                </div>
                    );
                })()}

                {/* View Toggle */}
                <div className="flex gap-2 mb-4">
                    <button
                        onClick={() => setViewMode('leaderboard')}
                        className={`flex items-center gap-2 px-4 py-2 rounded-lg ${
                            viewMode === 'leaderboard' 
                                ? 'bg-orange-500 text-white' 
                                : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                        }`}
                    >
                        <FaList /> Leaderboard
                    </button>
                    <button
                        onClick={() => setViewMode('graph')}
                        className={`flex items-center gap-2 px-4 py-2 rounded-lg ${
                            viewMode === 'graph'
                                ? 'bg-orange-500 text-white'
                                : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                        }`}
                    >
                        <FaProjectDiagram /> Network Graph
                    </button>
                </div>

                {/* Leaderboard View */}
                {viewMode === 'leaderboard' && (
                    <div className="bg-white rounded-lg shadow-sm border">
                        <div className="p-4 border-b">
                            <h4 className="text-lg font-semibold">{displayLabel} Leaderboard</h4>
                            <p className="text-sm text-gray-500">
                                Ranked by {displayLabel.toLowerCase()} received
                            </p>
                        </div>
                        <div className="overflow-x-auto">
                            <table className="w-full">
                                <thead className="bg-gray-50">
                                    <tr>
                                        <th className="px-4 py-3 text-left text-sm font-medium text-gray-600">Rank</th>
                                        <th className="px-4 py-3 text-left text-sm font-medium text-gray-600">Member</th>
                                        <th className="px-4 py-3 text-center text-sm font-medium text-gray-600">Received</th>
                                        <th className="px-4 py-3 text-center text-sm font-medium text-gray-600">Given</th>
                                        <th className="px-4 py-3 text-center text-sm font-medium text-gray-600">Status</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {leaderboard.map((member, index) => (
                                        <tr key={member._id} className="border-t hover:bg-gray-50">
                                            <td className="px-4 py-3">
                                                {index === 0 && member.receivedCount > 0 ? '🥇' : 
                                                 index === 1 && member.receivedCount > 0 ? '🥈' : 
                                                 index === 2 && member.receivedCount > 0 ? '🥉' : 
                                                 `#${index + 1}`}
                                            </td>
                                            <td className="px-4 py-3">
                                                <div className="flex items-center gap-2">
                                                    <div 
                                                        className="w-3 h-3 rounded-full"
                                                        style={{ backgroundColor: getMemberTypeColor(member.memberType) }}
                                                    />
                                                    <span className="font-medium">{member.fullName}</span>
                                                </div>
                                                <span className="text-xs text-gray-500">{member.email}</span>
                                            </td>
                                            <td className="px-4 py-3 text-center font-semibold text-green-600">
                                                {member.receivedCount}
                                            </td>
                                            <td className="px-4 py-3 text-center font-semibold text-blue-600">
                                                {member.givenCount}
                                            </td>
                                            <td className="px-4 py-3 text-center">
                                                {getMemberTypeBadge(member.memberType) && (
                                                    <span className="text-sm">
                                                        {getMemberTypeBadge(member.memberType)}
                                                    </span>
                                                )}
                                            </td>
                                        </tr>
                                    ))}
                                    {leaderboard.length === 0 && (
                                        <tr>
                                            <td colSpan={5} className="px-4 py-8 text-center text-gray-500">
                                                No {displayLabel.toLowerCase()} data yet
                                            </td>
                                        </tr>
                                    )}
                                </tbody>
                            </table>
                        </div>
                    </div>
                )}

                {/* Network Graph View */}
                {viewMode === 'graph' && (
                    <div className="bg-white rounded-lg shadow-sm border p-4">
                        <div className="mb-4">
                            <h4 className="text-lg font-semibold">{displayLabel} Network</h4>
                            <p className="text-sm text-gray-500 mb-2">
                                Arrows show who gave {displayLabel.toLowerCase()} to whom
                            </p>
                            <div className="flex gap-1 mb-3">
                                {[1, 2, 3, 4, 5, 6].map(m => (
                                    <button
                                        key={m}
                                        onClick={() => setGraphMonths(m)}
                                        className={`px-3 py-1 rounded text-sm ${
                                            graphMonths === m
                                                ? 'bg-orange-500 text-white'
                                                : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                                        }`}
                                    >
                                        {m}mo
                                    </button>
                                ))}
                            </div>
                            <div className="flex flex-wrap gap-4 text-sm">
                                <div className="flex items-center gap-2">
                                    <div className="w-4 h-4 rounded-full" style={{ backgroundColor: '#3B82F6' }} />
                                    <span>Member</span>
                                </div>
                                <div className="flex items-center gap-2">
                                    <div className="w-4 h-4 rounded-full" style={{ backgroundColor: '#22C55E' }} />
                                    <span>Hub ({displayHubThreshold}+ activity)</span>
                                </div>
                                <div className="flex items-center gap-2">
                                    <div className="w-4 h-4 rounded-full" style={{ backgroundColor: '#FFD700' }} />
                                    <span>Superhub ({displaySuperhubThreshold}+ activity)</span>
                                </div>
                                <div className="flex items-center gap-2">
                                    <div className="w-4 h-4 rounded-full" style={{ backgroundColor: '#F97316' }} />
                                    <span>Guests (combined)</span>
                                </div>
                            </div>
                        </div>
                        {/* cw: keep the <svg> mounted for the whole graph view so svgRef is always
                            attached. Previously the svg only rendered once graphData arrived, so when
                            landing directly in graph mode (back from a profile, or refresh with
                            ?view=graph) the render effect fired before the ref existed and the graph
                            came up blank. The empty-state is now an overlay instead of replacing the svg. */}
                        <div className="border rounded-lg overflow-hidden relative" style={{ minHeight: '600px' }}>
                            <svg ref={svgRef} className="w-full" style={{ minHeight: '600px' }} />
                            {!(graphData && graphData.nodes?.length > 0) && (
                                <div className="absolute inset-0 flex items-center justify-center text-gray-500">
                                    No network data available yet. Start giving {displayLabel.toLowerCase()}!
                                </div>
                            )}
                        </div>
                    </div>
                )}

                {/* Legend */}
                <div className="mt-6 bg-blue-50 rounded-lg p-4 border border-blue-200">
                    <h5 className="font-semibold text-blue-800 mb-2">How it works</h5>
                    <ul className="text-sm text-blue-700 space-y-1">
                        <li>• Give {displayLabel.toLowerCase()} to recognize fellow group members</li>
                        <li>• <span className="font-semibold">Hubs</span> (green): Members with {displayHubThreshold}+ total given + received</li>
                        <li>• <span className="font-semibold">Superhubs</span> (gold): Members with {displaySuperhubThreshold}+ total given + received</li>
                        <li>• Grow your network by giving and receiving {displayLabel.toLowerCase()}!</li>
                    </ul>
                </div>
            </div>
        </Layout>
    );
};

export default Testimonials;